import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Trash2, Edit, Plus, GripVertical, Eye, EyeOff } from 'lucide-react';
import { GlossarySourceCategory } from '../../types/glossary';
import { glossaryCategoryService } from '../../services/glossaryCategoryService';
import { toast } from 'react-hot-toast';

interface CategoryFormData {
  name: string;
  gid: string;
  description: string;
  isActive: boolean;
  order: number;
}

const initialFormData: CategoryFormData = {
  name: '',
  gid: '',
  description: '',
  isActive: true,
  order: 0
};

export const GlossaryCategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<GlossarySourceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<CategoryFormData>(initialFormData);
  const [editingCategory, setEditingCategory] = useState<GlossarySourceCategory | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await glossaryCategoryService.getAllCategories();
      setCategories(data.sort((a: GlossarySourceCategory, b: GlossarySourceCategory) => a.order - b.order));
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setFormData({
      ...initialFormData,
      order: categories.length
    });
    setIsDialogOpen(true);
  };

  const handleEditCategory = (category: GlossarySourceCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      gid: category.gid,
      description: '',
      isActive: category.isActive,
      order: category.order
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.gid.trim()) {
      toast.error('Name and GID are required');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingCategory) {
        // Update existing category
        await glossaryCategoryService.updateCategory(editingCategory.id!, formData);
        toast.success('Category updated successfully');
      } else {
        // Create new category
        await glossaryCategoryService.createCategory(formData);
        toast.success('Category created successfully');
      }
      
      setIsDialogOpen(false);
      setFormData(initialFormData);
      setEditingCategory(null);
      await loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (category: GlossarySourceCategory) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await glossaryCategoryService.deleteCategory(category.id!);
      toast.success('Category deleted successfully');
      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleToggleActive = async (category: GlossarySourceCategory) => {
    try {
      await glossaryCategoryService.updateCategory(category.id!, {
        isActive: !category.isActive
      });
      toast.success(`Category ${category.isActive ? 'deactivated' : 'activated'}`);
      await loadCategories();
    } catch (error) {
      console.error('Error toggling category status:', error);
      toast.error('Failed to update category status');
    }
  };

  const handleReorder = async (categoryId: string, newOrder: number) => {
    try {
      await glossaryCategoryService.reorderCategories(categoryId, newOrder);
      await loadCategories();
    } catch (error) {
      console.error('Error reordering category:', error);
      toast.error('Failed to reorder category');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Glossary Categories</CardTitle>
          <CardDescription>Loading categories...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Glossary Categories</CardTitle>
            <CardDescription>
              Manage glossary categories that correspond to Google Sheets tabs
            </CardDescription>
          </div>
          <Button onClick={handleCreateCategory}>
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {categories.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No categories found. Create your first category to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className="flex items-center justify-between p-4 border border-gray-700 rounded-lg bg-gray-800"
              >
                <div className="flex items-center space-x-4">
                  <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium">{category.name}</h3>
                      <Badge variant={category.isActive ? 'default' : 'secondary'}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">GID: {category.gid}</p>
                    {false && (
                      <p className="text-sm text-gray-500 mt-1">{category.name}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(category)}
                  >
                    {category.isActive ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditCategory(category)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCategory(category)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Category Form Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[425px] bg-gray-800 border-gray-700 text-white">
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Edit Category' : 'Create Category'}
              </DialogTitle>
              <DialogDescription>
                {editingCategory 
                  ? 'Update the category details below.'
                  : 'Add a new glossary category that corresponds to a Google Sheets tab.'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right text-white">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="col-span-3 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    placeholder="e.g., Useful Acronyms"
                    required
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="gid" className="text-right text-white">
                    GID
                  </Label>
                  <Input
                    id="gid"
                    value={formData.gid}
                    onChange={(e) => setFormData({ ...formData, gid: e.target.value })}
                    className="col-span-3 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    placeholder="e.g., 123456789"
                    required
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="description" className="text-right text-white">
                    Description
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="col-span-3 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="order" className="text-right text-white">
                    Order
                  </Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="col-span-3 bg-gray-700 border-gray-600 text-white"
                    min="0"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="isActive" className="text-right text-white">
                    Active
                  </Label>
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};